<template>
  <div>
    <div class="google-map" ref="googleMap"/>
    <template v-if="Boolean(this.google) && Boolean(this.map)">
      <slot
        :google="google"
        :map="map"
      />
    </template>
  </div>
</template>

<script>
import { Capacitor } from '@capacitor/core';
import { includes, debounce } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import eventBus from '@/services/eventBus'
import { getFullBoundsFromNESW } from '@/services/location'
import { bboxFromCircle, bboxFromPaths } from '@/services/geo/bounds'
import { bufferRing } from '@/services/geo/polygonBuffer'
import { modeForRadius } from '@/services/geo/searchAreaMode'

export default {
  name: 'GoogleMapLoader',

  props: {
    mapConfig: Object,
    apiKey: String,
    google: Object,
  },

  data: () => ({
    map: null,
    boundsSet: false,
    boundsListenerSet: false,
    dragEndListenerSet: false,
    zoomOutButtonListenerSet: false,
    localBounds: null,
    currentBounds: null,
    topLeft: null,
    bottomRight: null,
  }),

  async mounted() {
    // adding eventBus listener
    eventBus.$on('initialize-map', () => {
      this.initializeMap()
    })
    eventBus.$on('reinitialize-map', () => {
      this.reinitializeMap()
    })
  },
  beforeDestroy() {
    // removing eventBus listener
    eventBus.$off('initialize-map')
    eventBus.$off('reinitialize-map')
    this.removeIdelListener()
  },

  computed: {
    ...mapGetters('map', [
      'mapBounds',
      'geoBoundingBox',
      'searchBounds',
      'routeSetup',
      'isSearchAreaLocked',
      'searchAreaCircle',
      'searchAreaBasePaths',
    ]),
    ...mapGetters('location', [
      'useCurrentLocation',
      'selectedRadius',
    ]),
    ...mapGetters('garden', [
      'loadingGardenList',
    ]),

    currentZoomLevel() {
      return this.map ? this.map.getZoom() : null
    },

    /**
     * TRBRBLTL viewport bounds that fit the current county search area
     * overlay. Returns null when no county search is active.
     *
     * Circle mode  -> bbox of the geo_distance circle.
     * Polygon mode -> bbox of the buffered ring (at 1mi/5mi) or the base
     *                 outline (at radius 0).
     *
     * We can't reuse `searchBounds` for this because that's the old
     * Google-viewport-driven bounds; for county searches the user-visible
     * shape is the overlay, so the map must be fit to *its* bounds.
     */
    searchAreaViewport() {
      if (this.searchAreaCircle) {
        return bboxFromCircle(this.searchAreaCircle.center, this.searchAreaCircle.radiusMeters)
      }
      if (this.searchAreaBasePaths) {
        const miles = Number(this.selectedRadius?.value)
        const mode = modeForRadius(miles)
        if (mode === 'polygon') {
          const buffered = bufferRing(this.searchAreaBasePaths, miles)
          if (buffered) return bboxFromPaths(buffered)
        }
        return bboxFromPaths(this.searchAreaBasePaths)
      }
      return null
    },
  },

  watch: {
    async localBounds(newBounds, oldBounds) {
      if (newBounds !== oldBounds) {
        if (!this.boundsSet) {
          this.addMapBounds(newBounds)
          this.boundsSet = true
        } else if (this.$route.name === 'List' && !this.isSearchAreaLocked) {
          if (this.searchBounds) {
            // D5: a user pan/zoom on a location search re-searches the live
            // viewport. searchLiveViewport updates searchBounds to the live
            // viewport before refetching, so the API request, the URL, and the
            // visible viewport all agree (previously the API kept using the
            // stale searchBounds while the URL was rewritten to the viewport).
            await this.searchLiveViewport(newBounds)
          } else {
            this.updateMapSearch(true)

            this.addAndFilterBounds(newBounds)
          }
        }
      }
    },

    useCurrentLocation(value) {
      const platform = Capacitor.getPlatform();

      if (!value && (platform === 'android' || platform === 'ios')) {
        this.removeIdelListener();
      }
    },

    async geoBoundingBox(newGeoBoundingBox, oldGeoBoundingBox) {
      if (newGeoBoundingBox && newGeoBoundingBox !== oldGeoBoundingBox && this.routeSetup && this.map) {
        if (this.searchBounds) {
          await this.setSearchBounds()
        } else {
          await this.setGeoBounds(newGeoBoundingBox)
        }
      }
    },

    async map(value) {
      if (value && (this.geoBoundingBox || this.searchBounds)) {
        if (this.searchBounds && this.$route.name !== 'Garden') {
          await this.setSearchBounds()
        } else {
          if (this.geoBoundingBox && this.$route.name !== 'Garden') {
            await this.setGeoBounds(this.geoBoundingBox)
          }
        }
      }
    },

    /**
     * Refit the map to a county search area whenever the overlay
     * changes (new county, or radius changed within polygon/circle
     * mode). Unlike setSearchBounds, this does NOT destroy and recreate
     * the map - that would orphan the polygon/circle overlay managed
     * by SearchAreaPolygon.vue. It just fitBounds + setZoom in place.
     */
    searchAreaViewport(newBounds, oldBounds) {
      if (!newBounds || !this.map || this.$route.name === 'Garden') return
      if (oldBounds
        && newBounds.top_right.lat === oldBounds.top_right.lat
        && newBounds.top_right.lng === oldBounds.top_right.lng
        && newBounds.bottom_left.lat === oldBounds.bottom_left.lat
        && newBounds.bottom_left.lng === oldBounds.bottom_left.lng) return
      this.fitMapToSearchAreaViewport(newBounds)
    },
  },

  methods: {
    ...mapActions('map', [
      'addMapBounds',
      'addAndFilterBounds',
      'releaseSearchAreaForDrag',
      'searchLiveViewport',
    ]),

    ...mapActions('garden', [
      'clearSearch',
      'updateMapSearch',
    ]),

    initializeMap() {
      return new Promise((resolve) => {
        const mapContainer = this.$refs.googleMap

        if (this.google) {
          this.map = new this.google.maps.Map(mapContainer, this.mapConfig);
          this.initZoomOutButton();
        }

        if (!this.geoBoundingBox || this.$route.name === 'Garden') {
          this.setBoundsFromPoint();
        }

        resolve();
      });
    },

    async initZoomOutButton() {
      const zoomOutButton = document.querySelector('.zoom-out-button');

      if (zoomOutButton) {
        if (this.zoomOutButtonListenerSet) {
          await this.removeZoomOutButtonlListener(zoomOutButton)
        }
        if (!this.zoomOutButtonListenerSet) {
          zoomOutButton.addEventListener('click', async () => {
            const currentZoom = this.map.getZoom();

            let zoomedOut = currentZoom - 1;

            if (window.outerWidth < 840) {
              zoomedOut = currentZoom - 2;
            }

            // Clamp
            if (currentZoom === this.mapConfig) {
              zoomedOut = currentZoom;
            }

            await this.map.setZoom(zoomedOut);

            if (this.searchBounds) {
              await this.clearSearch()
            }

            // Mirror the drag override: without releasing the county lock,
            // the localBounds write below can't broaden an empty result set.
            if (this.$route.name === 'List' && this.isSearchAreaLocked) {
              this.releaseSearchAreaForDrag()
            }

            this.currentBounds = this.map.getBounds()

            this.localBounds = getFullBoundsFromNESW({
              northeast: this.currentBounds.getNorthEast(),
              southwest: this.currentBounds.getSouthWest(),
            })
          });
          this.zoomOutButtonListenerSet = true
        }
      }
    },
    removeZoomOutButtonlListener(zoomOutButton) {
      return new Promise((resolve) => {
        zoomOutButton.removeEventListener('click', () => {
          this.zoomOutButtonListenerSet = false
        })
        resolve()
      })
    },

    async reinitializeMap() {
      this.boundsSet = null
      await this.removeIdelListener()

      this.map = null
      await this.initializeMap()

      await this.setSearchBounds()
    },

    setBoundsFromPoint() {
      const bounds = new this.google.maps.LatLngBounds();
      const latLng = new google.maps.LatLng(this.mapConfig.center);

      bounds.extend(latLng);
      this.map.fitBounds(bounds)
      if (this.$route.name === 'Garden') {
        this.map.setZoom(16)

        this.currentBounds = bounds

        this.localBounds = getFullBoundsFromNESW({
          northeast: this.currentBounds.getNorthEast(),
          southwest: this.currentBounds.getSouthWest(),
        })
      } else {
        this.addIdleListener();
      }
    },

    setCurrentAndLocalBounds() {
      if (!this.loadingGardenList) {
        this.localBounds = getFullBoundsFromNESW({
          northeast: this.currentBounds.getNorthEast(),
          southwest: this.currentBounds.getSouthWest(),
        })
      }
    },

    addIdleListener() {
      if (!this.boundsListenerSet && this.map.getBounds() && !includes(['Garden'], this.$route.name)) {
        this.google.maps.event.addListener(this.map, 'idle', debounce(() => {
          this.currentBounds = this.map.getBounds()

          this.setCurrentAndLocalBounds()
        }, 100))

        this.boundsListenerSet = true
      }
      this.addDragEndListener()
    },

    /**
     * Option B drag-override (D2), pan-only. `dragend` fires ONLY on a genuine
     * user drag - never on programmatic fitBounds/setZoom, and never on a
     * scroll-zoom - so this is the safe seam to release a county search lock
     * without looping on the overlay's own refit. When a county search has
     * locked the area and the user pans, we release the lock + overlay ONLY.
     * The viewport search then runs exactly once via the existing debounced
     * `idle` listener (which Google fires right after `dragend`), now that the
     * area is unlocked - so we do not double-fetch here. Non-county drags need
     * no special handling: the same idle path already refetches for them.
     */
    addDragEndListener() {
      if (this.dragEndListenerSet || !this.map || !this.google) return
      this.google.maps.event.addListener(this.map, 'dragend', () => {
        if (this.$route.name !== 'List' || !this.isSearchAreaLocked) return
        this.releaseSearchAreaForDrag()
      })
      this.dragEndListenerSet = true
    },
    removeIdelListener() {
      return new Promise((resolve) => {
        if (this.boundsListenerSet && this.map && this.google) {
          this.google.maps.event.addListener(this.map, 'idle')

          this.boundsListenerSet = false
        }
        resolve()
      })
    },

    setGeoBounds(geoBoundingBox) {
      return new Promise((resolve) => {
        const topLeft = new this.google.maps.LatLng(geoBoundingBox.top_left.lat, geoBoundingBox.top_left.lng)
        const bottomRight = new this.google.maps.LatLng(geoBoundingBox.bottom_right.lat, geoBoundingBox.bottom_right.lng)
        const boundingBox = new this.google.maps.LatLngBounds(topLeft, bottomRight)

        this.map.fitBounds(boundingBox)
        this.google.maps.event.addListenerOnce(this.map, 'bounds_changed', function () {
          this.currentBounds = this.map.getBounds()
          if (this.$route.name === 'Garden') {
            this.map.setZoom(16)
          } else {
            this.addIdleListener()
          }
        }.bind(this));
        resolve();
      });
    },

    setSearchBounds() {
      return new Promise((resolve) => {
        this.fitBoundsWithFractionalZoom(this.searchBounds, { addIdleListener: true })
        resolve()
      })
    },

    /**
     * Fit the map to a county search area overlay without destroying it.
     * Same fractional-zoom math as setSearchBounds, but the bounds come
     * from `searchAreaViewport` (the polygon or circle the user sees),
     * not from `searchBounds`.
     */
    fitMapToSearchAreaViewport(viewport) {
      this.fitBoundsWithFractionalZoom(viewport, { addIdleListener: false })
    },

    /**
     * Shared zoom application used by both the standard location-search
     * path and the county search-area refit. fitBounds picks an integer
     * zoom; we then compute a precise fractional zoom from the bounds
     * span (Mercator-corrected on the latitude axis) and apply it.
     *
     * @param {object} bounds TRBRBLTL bounds.
     * @param {{ addIdleListener?: boolean }} options
     */
    fitBoundsWithFractionalZoom(bounds, { addIdleListener = false } = {}) {
      const neLat = bounds.top_right.lat
      const neLng = bounds.top_right.lng
      const swLat = bounds.bottom_left.lat
      const swLng = bounds.bottom_left.lng

      const northeast = new this.google.maps.LatLng(neLat, neLng)
      const southWest = new this.google.maps.LatLng(swLat, swLng)
      const boundingBox = new this.google.maps.LatLngBounds(southWest, northeast)

      this.map.fitBounds(boundingBox)

      this.google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        const mapDiv = this.map.getDiv()
        const mapWidth = mapDiv.clientWidth
        const mapHeight = mapDiv.clientHeight

        // Longitude zoom (no Mercator distortion needed).
        const lngSpan = Math.abs(neLng - swLng)
        const lngZoom = Math.log2((mapWidth * 360) / (256 * lngSpan))

        // Latitude zoom (account for Mercator projection).
        const toRad = (deg) => (deg * Math.PI) / 180
        const mercatorY = (lat) => Math.log(Math.tan((Math.PI / 4) + (toRad(lat) / 2)))
        const mercatorSpan = Math.abs(mercatorY(neLat) - mercatorY(swLat))
        const latZoom = Math.log2((mapHeight * 2 * Math.PI) / (256 * mercatorSpan))

        // Use the more zoomed-out axis so both dimensions fit, less a
        // small margin for visual padding.
        const exactZoom = Math.min(lngZoom, latZoom) - 0.15
        const clampedZoom = Math.max(
          this.mapConfig?.minZoom || 6,
          Math.min(exactZoom, this.mapConfig?.maxZoom || 16),
        )

        this.map.setZoom(clampedZoom)
        if (addIdleListener) this.addIdleListener()
      })
    },
  },
}

</script>

<style scoped>

.google-map {
  width: 100%;
  min-height: 100%;
}
</style>
